import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  addDays,
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { Check, ChevronLeft } from "lucide-react";

import { Icon } from "@/lib/Icon";
import { track } from "@/lib/analytics";
import {
  useBookGuidedSelfInjection,
  useGuidedSelfInjectionAvailability,
  useRescheduleGuidedSelfInjection,
} from "@/lib/api/hooks";
import { cn } from "@/lib/cn";
import { clinicianInitials, renderableClinicianPhoto } from "../clinicianPhoto";

interface GuidedInjectionBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicianName: string;
  clinicianAvatarUrl: string | null;
  expectations: string[];
  earliestBookingAt?: string | null;
  experimentKey: string;
  variantKey: string;
  entitlementKey: string;
  rescheduleAppointmentId?: string | null;
}

/**
 * Booking surface dedicated to the paid nurse-guided injection entitlement.
 * It intentionally does not use the ordinary BookingDialog or its endpoints:
 * the backend route binds every slot and booking to the configured nurse's
 * guided-injection type, while the regular route remains bound to sync visits.
 */
export function GuidedInjectionBookingDialog({
  open,
  onOpenChange,
  clinicianName,
  clinicianAvatarUrl,
  expectations,
  earliestBookingAt = null,
  experimentKey,
  variantKey,
  entitlementKey,
  rescheduleAppointmentId = null,
}: GuidedInjectionBookingDialogProps) {
  const availability = useGuidedSelfInjectionAvailability({
    days: 28,
    enabled: open,
  });
  const book = useBookGuidedSelfInjection();
  const reschedule = useRescheduleGuidedSelfInjection();
  const isReschedule = Boolean(rescheduleAppointmentId);
  const pending = book.isPending || reschedule.isPending;
  const error = (isReschedule ? reschedule.error : book.error) as {
    message?: string;
  } | null;

  const [monthCursor, setMonthCursor] = useState(() =>
    startOfMonth(new Date()),
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  const slotsByDate = useMemo(() => {
    const grouped = new Map<string, NonNullable<typeof availability.data>>();
    for (const slot of availability.data ?? []) {
      const key = format(parseISO(slot.start_at), "yyyy-MM-dd");
      const existing = grouped.get(key) ?? [];
      existing.push(slot);
      grouped.set(key, existing);
    }
    return grouped;
  }, [availability.data]);

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);
  const thisMonth = startOfMonth(today);
  const gridDays = useMemo(() => {
    const first = startOfWeek(monthCursor, { weekStartsOn: 1 });
    return Array.from({ length: 42 }, (_, index) => addDays(first, index));
  }, [monthCursor]);
  const selectedSlots = selectedDate
    ? (slotsByDate.get(format(selectedDate, "yyyy-MM-dd")) ?? [])
    : [];

  const reset = () => {
    setSelectedDate(null);
    setSelectedSlotId(null);
  };

  const handleConfirm = async () => {
    if (!selectedSlotId || pending) return;
    if (isReschedule && rescheduleAppointmentId) {
      await reschedule.mutateAsync({
        appointmentId: rescheduleAppointmentId,
        datetime: selectedSlotId,
      });
      track("guided_injection_appointment_rescheduled", {
        appointment_id: rescheduleAppointmentId,
        experiment_key: experimentKey,
        variant_key: variantKey,
        entitlement_key: entitlementKey,
      });
    } else {
      const appointment = await book.mutateAsync({ slot_id: selectedSlotId });
      track("guided_injection_appointment_booked", {
        appointment_id: appointment.id,
        experiment_key: experimentKey,
        variant_key: variantKey,
        entitlement_key: entitlementKey,
      });
    }
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-black/10 backdrop-blur-[10px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-dialog flex max-h-[90vh] w-[min(54rem,calc(100vw-20px))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden border border-line-subtle bg-page">
          <header className="flex h-40 items-center border-b border-line-subtle bg-[#F0F0F0]">
            <div className="flex min-w-0 flex-1 items-center px-15">
              <Dialog.Title className="font-mono text-12 uppercase leading-105 text-[#5E5E5E]">
                {isReschedule
                  ? "RESCHEDULE GUIDED INJECTION"
                  : "BOOK YOUR GUIDED INJECTION"}
              </Dialog.Title>
            </div>
            <div className="h-40 p-[2px]">
              <Dialog.Close
                aria-label="Close"
                className="flex h-full w-[36px] items-center justify-center bg-slate hover:bg-[#CCCCCC]"
              >
                <span className="relative block h-[14px] w-[14px]">
                  <span className="absolute left-0 top-1/2 h-[1.5px] w-[14px] origin-center rotate-45 bg-black" />
                  <span className="absolute left-0 top-1/2 h-[1.5px] w-[14px] origin-center -rotate-45 bg-black" />
                </span>
              </Dialog.Close>
            </div>
          </header>

          <Dialog.Description className="border-b border-line-subtle bg-sunken px-15 py-12 type-body-sm text-ink-secondary">
            Choose an available time now. Appointments begin five days after
            purchase so your medication has time to arrive.
            {earliestBookingAt && (
              <span className="mt-5 block type-label-xs text-ink">
                APPOINTMENTS AVAILABLE FROM{" "}
                {format(parseISO(earliestBookingAt), "MMMM d, yyyy")}
              </span>
            )}
          </Dialog.Description>

          <div className="flex items-center gap-12 border-b border-line-subtle px-15 py-12">
            <ClinicianPhoto src={clinicianAvatarUrl} name={clinicianName} />
            <div>
              <p className="type-label-sm text-ink">WITH {clinicianName}</p>
              <p className="mt-3 type-body-sm text-ink-secondary">
                A private video visit with your nurse
              </p>
            </div>
          </div>

          {expectations.length > 0 && (
            <ul className="grid gap-6 border-b border-line-subtle bg-sunken px-15 py-11 sm:grid-cols-2">
              {expectations.map((expectation) => (
                <li
                  key={expectation}
                  className="flex items-start gap-6 type-body-sm text-ink-secondary"
                >
                  <Check
                    size={13}
                    strokeWidth={1.5}
                    className="mt-1 shrink-0 text-success"
                    aria-hidden
                  />
                  {expectation}
                </li>
              ))}
            </ul>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {!selectedDate ? (
              <div className="flex flex-col">
                <div className="grid grid-cols-[45px_1fr_45px] items-center border-b border-line-subtle">
                  <button
                    type="button"
                    aria-label="Previous month"
                    disabled={monthCursor <= thisMonth}
                    onClick={() => {
                      const previous = addMonths(monthCursor, -1);
                      if (previous >= thisMonth) setMonthCursor(previous);
                    }}
                    className="flex h-45 items-center justify-center text-ink disabled:text-ink-muted"
                  >
                    <ChevronLeft size={16} strokeWidth={1.5} />
                  </button>
                  <p className="text-center type-label-md text-ink">
                    {format(monthCursor, "MMMM yyyy").toUpperCase()}
                  </p>
                  <button
                    type="button"
                    aria-label="Next month"
                    onClick={() => setMonthCursor(addMonths(monthCursor, 1))}
                    className="flex h-45 items-center justify-center text-ink"
                  >
                    <span className="inline-flex w-8 items-center justify-center">
                      <Icon name="Arrow" viewBox="0 0 10 12" />
                    </span>
                  </button>
                </div>

                <div className="grid grid-cols-7 border-b border-line-subtle">
                  {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map(
                    (weekday) => (
                      <div
                        key={weekday}
                        className="py-12 text-center type-label-xs text-ink-muted"
                      >
                        {weekday}
                      </div>
                    ),
                  )}
                </div>

                <div className="grid grid-cols-7">
                  {availability.isLoading
                    ? Array.from({ length: 42 }, (_, index) => (
                        <div
                          key={index}
                          className="h-50 animate-pulse bg-sunken opacity-50"
                        />
                      ))
                    : gridDays.map((date) => {
                        const inMonth = isSameMonth(date, monthCursor);
                        const available =
                          date >= today &&
                          (slotsByDate.get(format(date, "yyyy-MM-dd"))
                            ?.length ?? 0) > 0;
                        return (
                          <button
                            key={date.toISOString()}
                            type="button"
                            disabled={!available}
                            aria-label={format(date, "MMMM d, yyyy")}
                            onClick={() => {
                              setSelectedDate(date);
                              setSelectedSlotId(null);
                            }}
                            className={cn(
                              "flex h-50 items-center justify-center type-body-md",
                              available
                                ? "cursor-pointer text-ink hover:bg-sunken"
                                : "cursor-not-allowed text-ink-muted",
                              !inMonth && "opacity-40",
                              isSameDay(date, today) &&
                                "ring-1 ring-inset ring-ink",
                            )}
                          >
                            {format(date, "d")}
                          </button>
                        );
                      })}
                </div>

                <div className="border-t border-line-subtle px-15 py-20 text-center type-label-sm text-ink-muted">
                  {availability.isError
                    ? "AVAILABILITY COULD NOT BE LOADED. TRY AGAIN."
                    : `SELECT A DATE TO VIEW ${clinicianName.toUpperCase()}’S AVAILABILITY`}
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDate(null);
                    setSelectedSlotId(null);
                  }}
                  className="flex items-center gap-8 border-b border-line-subtle px-15 py-12 type-label-sm text-ink-secondary hover:text-ink"
                >
                  <ChevronLeft size={14} strokeWidth={1.5} />
                  BACK TO CALENDAR
                </button>
                <div className="px-15 py-20">
                  <p className="type-title-sm">
                    {format(selectedDate, "EEEE, MMMM d")}
                  </p>
                  <p className="mt-5 type-body-sm text-ink-secondary">
                    Times are shown in your local timezone.
                  </p>
                  <div className="mt-20 grid grid-cols-2 gap-8 sm:grid-cols-3">
                    {selectedSlots.map((slot) => (
                      <button
                        type="button"
                        key={slot.id}
                        onClick={() => setSelectedSlotId(slot.id)}
                        className={cn(
                          "h-40 border type-label-sm",
                          selectedSlotId === slot.id
                            ? "border-ink bg-ink text-white"
                            : "border-line-default bg-page text-ink hover:border-ink",
                        )}
                      >
                        {format(parseISO(slot.start_at), "h:mm a")}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {selectedDate && (
            <div className="border-t border-line-subtle bg-page">
              {error && (
                <p
                  role="alert"
                  className="border-b border-line-subtle px-15 py-10 type-body-sm text-danger"
                >
                  {error.message ?? "The appointment could not be saved."}
                </p>
              )}
              <button
                type="button"
                disabled={!selectedSlotId || pending}
                onClick={() => void handleConfirm()}
                className="flex w-full items-center justify-between bg-ink px-15 py-18 type-body-md text-white disabled:opacity-40"
              >
                {pending
                  ? "Confirming…"
                  : isReschedule
                    ? "Confirm new time"
                    : `Confirm with ${clinicianName.split(" ")[0]}`}
                <span className="inline-flex w-8 items-center justify-center">
                  <Icon name="Arrow" viewBox="0 0 10 12" />
                </span>
              </button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ClinicianPhoto({ src, name }: { src: string | null; name: string }) {
  const photo = renderableClinicianPhoto(src);
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        className="h-44 w-44 rounded-full border border-line-subtle object-cover"
      />
    );
  }
  return (
    <div
      aria-label={name}
      className="flex h-44 w-44 items-center justify-center rounded-full bg-sunken type-label-sm text-ink-secondary"
    >
      {clinicianInitials(name)}
    </div>
  );
}
